import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { endpoints } from 'config/api';
import {
  Lead,
  PaginationParams,
  PaginatedLeadsResponse,
  CreateLeadDto,
  UpdateLeadDto,
  UpdateLeadStatusDto,
  AssignLeadDto,
  AddNoteDto,
  UpdateLeadBudgetDto,
  UpdateSqftCalculatorDto,
} from './leadsTypes';

export const leadsApi = createApi({
  reducerPath: 'leadsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Leads', 'Lead', 'LeadDocuments'],
  endpoints: (builder) => ({
    fetchLeads: builder.query<PaginatedLeadsResponse, PaginationParams | void>({
      query: (params) => ({
        url: endpoints.leads.base,
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedLeadsResponse) => response,
      providesTags: ['Leads'],
    }),

    getLead: builder.query<Lead, string>({
      query: (id) => ({
        url: `${endpoints.leads.base}/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: Lead) => response,
      providesTags: (_res, _err, id) => [{ type: 'Lead', id }],
    }),

    createLead: builder.mutation<Lead, CreateLeadDto>({
      query: (data) => ({
        url: endpoints.leads.base,
        method: 'POST',
        data,
      }),
      transformResponse: (response: Lead) => response,
      invalidatesTags: ['Leads'],
    }),

    updateLead: builder.mutation<Lead, { id: string; updatedLead: UpdateLeadDto }>({
      query: ({ id, updatedLead }) => ({
        url: `${endpoints.leads.base}/${id}`,
        method: 'PUT',
        data: updatedLead,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Lead', id },
        'Leads',
      ],
    }),

    updateLeadStatus: builder.mutation<Lead, { id: string; status: UpdateLeadStatusDto }>({
      query: ({ id, status }) => ({
        url: `${endpoints.leads.base}/${id}/status`,
        method: 'PATCH',
        data: status,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Lead', id },
        'Leads',
      ],
    }),

    assignLead: builder.mutation<Lead, { id: string; assignDto: AssignLeadDto }>({
      query: ({ id, assignDto }) => ({
        url: `${endpoints.leads.base}/${id}/assign`,
        method: 'PATCH',
        data: assignDto,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Lead', id },
        'Leads',
      ],
    }),

    addNoteToLead: builder.mutation<Lead, { id: string; note: AddNoteDto }>({
      query: ({ id, note }) => ({
        url: `${endpoints.leads.base}/${id}/notes`,
        method: 'POST',
        data: note,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Lead', id },
      ],
    }),

    deleteLead: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `${endpoints.leads.base}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Leads'],
    }),

    updateLeadBudget: builder.mutation<Lead, { id: string; budget: UpdateLeadBudgetDto }>({
      query: ({ id, budget }) => ({
        url: `${endpoints.leads.base}/${id}/budget`,
        method: 'PUT',
        data: budget,
      }),
      transformResponse: (response: Lead) => response,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Lead', id },
        'Leads',
      ],
    }),

    // Lead Documents
    uploadLeadDocument: builder.mutation<any, { leadId: string; formData: FormData }>({
      query: ({ leadId, formData }) => ({
        url: `${endpoints.leads.base}/${leadId}/documents/upload`,
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
      invalidatesTags: (_res, _err, { leadId }) => [
        { type: 'Lead', id: leadId },
        'LeadDocuments',
      ],
    }),

    deleteLeadDocument: builder.mutation<any, { leadId: string; documentId: string }>({
      query: ({ leadId, documentId }) => ({
        url: `${endpoints.leads.base}/${leadId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, { leadId }) => [
        { type: 'Lead', id: leadId },
        'LeadDocuments',
      ],
    }),

    getLeadDocumentUrl: builder.query<{ url: string; fileName: string; contentType: string; size: number }, { leadId: string; documentId: string }>({
      query: ({ leadId, documentId }) => ({
        url: `${endpoints.leads.base}/${leadId}/documents/${documentId}/url`,
        method: 'GET',
      }),
      transformResponse: (response: { url: string; fileName: string; contentType: string; size: number }) => response,
    }),

    // Lead Contacts
    addLeadContact: builder.mutation<any, { leadId: string; contact: any }>({
      query: ({ leadId, contact }) => ({
        url: `${endpoints.leads.base}/${leadId}/contacts`,
        method: 'POST',
        data: contact,
      }),
      invalidatesTags: (_res, _err, { leadId }) => [
        { type: 'Lead', id: leadId },
      ],
    }),

    updateLeadContact: builder.mutation<any, { leadId: string; contactId: string; contact: any }>({
      query: ({ leadId, contactId, contact }) => ({
        url: `${endpoints.leads.base}/${leadId}/contacts/${contactId}`,
        method: 'PUT',
        data: contact,
      }),
      invalidatesTags: (_res, _err, { leadId }) => [
        { type: 'Lead', id: leadId },
      ],
    }),

    // SqFt Calculator
    updateSqftCalculator: builder.mutation<Lead, { id: string; sqftCalculator: UpdateSqftCalculatorDto }>({
      query: ({ id, sqftCalculator }) => ({
        url: `${endpoints.leads.base}/${id}/sqft-calculator`,
        method: 'PATCH',
        data: sqftCalculator,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Lead', id },
      ],
    }),

    deleteLeadContact: builder.mutation<any, { leadId: string; contactId: string }>({
      query: ({ leadId, contactId }) => ({
        url: `${endpoints.leads.base}/${leadId}/contacts/${contactId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, { leadId }) => [
        { type: 'Lead', id: leadId },
      ],
    }),
  }),
});

export const {
  useFetchLeadsQuery,
  useLazyFetchLeadsQuery,
  useGetLeadQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useUpdateLeadStatusMutation,
  useAssignLeadMutation,
  useAddNoteToLeadMutation,
  useDeleteLeadMutation,
  useUpdateLeadBudgetMutation,
  useUploadLeadDocumentMutation,
  useDeleteLeadDocumentMutation,
  useGetLeadDocumentUrlQuery,
  useLazyGetLeadDocumentUrlQuery,
  // Lead Contacts
  useAddLeadContactMutation,
  useUpdateLeadContactMutation,
  useDeleteLeadContactMutation,
  // SqFt Calculator
  useUpdateSqftCalculatorMutation,
} = leadsApi;
